// Proxy media URLs, and a lazy probe of asset lengths for the tiles and rows in view.
// ponytail: the length is probed in the browser; store the ffprobe ingest duration on the server instead.
import { withAuthToken } from "@/scripts/keycloak.js";

const MAX_PROBES = 3;
const PROBE_TIMEOUT_MS = 15000;
const queue = [];
let running = 0;

export function proxyUrl(id) {
  return withAuthToken(process.env.VUE_APP_API_ENDPOINT + "/v1/fileServlet/proxy?id=" + id);
}

/** Resolves the proxy's length in seconds, or null (timeout, 404, no finite length). */
export function probeDuration(id) {
  return new Promise((resolve) => {
    queue.push({ id, resolve });
    startProbes();
  });
}

function startProbes() {
  while (running < MAX_PROBES && queue.length) {
    const { id, resolve } = queue.shift();
    running++;
    probe(id).then((secs) => {
      running--;
      resolve(secs);
      startProbes();
    });
  }
}

// The timeout starts here, not at queueing: a long queue behind the limit must not expire unprobed.
function probe(id) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    let timer = null;
    const settle = (secs) => {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
      video.onloadedmetadata = video.onerror = null;
      video.removeAttribute("src");
      video.load(); // stops the download
      resolve(Number.isFinite(secs) && secs > 0 ? secs : null);
    };
    timer = setTimeout(() => settle(null), PROBE_TIMEOUT_MS);
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () => settle(video.duration);
    video.onerror = () => settle(null);
    video.src = proxyUrl(id); // built at start, so the token is fresh
  });
}

// ponytail: one observer on the viewport, so nothing is probed ahead of scrolling. To look ahead,
// give each scroller its own observer (whenVisible(el, fn, root), rootMargin "200px 0px").
const watched = new Map();
let observer = null;

/** Calls fn once, the first time el scrolls into view. */
export function whenVisible(el, fn) {
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const callback = watched.get(entry.target);
        stopWatching(entry.target);
        if (callback) callback();
      }
    });
  }
  watched.set(el, fn);
  observer.observe(el);
}

export function stopWatching(el) {
  watched.delete(el);
  if (observer) observer.unobserve(el);
}
